import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addMonths } from "date-fns";
import { toast } from "sonner";
import { FlaskConical, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Seo } from "@/components/site/Section";
import { SubscriptionCard } from "@/components/account/SubscriptionCard";
import { PauseDialog, SkipDialog, ChangePlanDialog, CancelDialog, fmt } from "@/components/account/ActionDialogs";
import { AddressDialog } from "@/components/account/AddressDialog";
import { AddressCard, BillingCard, ExtrasCard, SupportCard } from "@/components/account/SideCards";
import { DeliveriesTable, PaymentsTable } from "@/components/account/HistoryTables";
import { SettingsPanel } from "@/components/account/SettingsPanel";
import { usePlans } from "@/lib/api";
import { makeDemo, shiftMonths } from "@/data/demo";

const DemoBanner = () => (
  <div className="flex items-start gap-3 rounded-2xl border border-[#D4A437]/50 bg-[#D4A437]/10 p-4 text-sm text-[#F3E0A2]" role="note" data-testid="demo-banner">
    <FlaskConical className="h-5 w-5 shrink-0" />
    <p><strong>Demonstration data.</strong> This is a preview of My WeeLogRun using sample information. Actions are simulated in your browser only — no real subscription is changed and nothing is charged.</p>
  </div>
);

const demoToast = (msg) => toast.success(msg, { description: "Demo only — in the live account we'll also email you a confirmation." });

export default function AccountDemo() {
  const initial = useMemo(makeDemo, []);
  const [customer, setCustomer] = useState(initial.customer);
  const [sub, setSub] = useState(initial.subscription);
  const [dialog, setDialog] = useState(null);
  const [tab, setTab] = useState("deliveries");
  const { data: plans = [] } = usePlans();
  const plan = plans.find((p) => p.slug === sub.plan);
  const pendingPlan = plans.find((p) => p.slug === sub.pendingPlan);
  const close = () => setDialog(null);
  const dlg = (k) => ({ open: dialog === k, onOpenChange: (o) => !o && close(), sub });

  const pause = (n) => {
    setSub({ ...sub, pause: { from: sub.nextBilling, until: addMonths(sub.nextBilling, n), months: n } });
    close();
    demoToast(`Paused for ${n} month${n > 1 ? "s" : ""}. Resumes ${fmt(addMonths(sub.nextBilling, n))}.`);
  };
  const resume = () => {
    setSub({ ...sub, pause: null, cancelsOn: null });
    demoToast("Your subscription is active again.");
  };
  const skip = () => {
    setSub({ ...shiftMonths(sub, 1), skipped: true });
    close();
    demoToast("Next delivery skipped. You won't be charged for it.");
  };
  const changePlan = (slug) => {
    setSub({ ...sub, pendingPlan: slug === sub.plan ? null : slug });
    close();
    demoToast("Plan change scheduled for your next billing date.");
  };
  const cancel = () => {
    setSub({ ...sub, cancelsOn: sub.nextBilling, pendingPlan: null });
    close();
    demoToast(`Cancellation confirmed. Takes effect ${fmt(sub.nextBilling)}.`);
  };

  const showInvoices = () => {
    setTab("payments");
    document.getElementById("account-history")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="lc-container pt-28 pb-24" data-testid="account-dashboard">
      <Seo title="My WeeLogRun (demo)" noindex />
      <DemoBanner />
      <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="lc-eyebrow">My WeeLogRun</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="account-welcome">Welcome back, {customer.name.split(" ")[0]}.</h1>
        </div>
        <Link to="/account" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F7F5F0]" data-testid="account-signout"><LogOut className="h-4 w-4" /> Exit demo</Link>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8">
          <SubscriptionCard sub={sub} plan={plan} pendingPlan={pendingPlan} open={setDialog} onResume={resume} />
          <div id="account-history" className="mt-6 scroll-mt-28 rounded-3xl border border-[#262626] bg-[#101010] p-4 sm:p-6">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-[#0a0a0a] p-1">
                {[["deliveries", "Deliveries"], ["payments", "Payments"], ["settings", "Account settings"]].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} className="rounded-full px-4 py-2 data-[state=active]:bg-[#FF6A00] data-[state=active]:text-[#080808]" data-testid={`account-tab-${v}`}>{l}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="deliveries" className="mt-5"><DeliveriesTable rows={initial.deliveries} /></TabsContent>
              <TabsContent value="payments" className="mt-5"><PaymentsTable rows={initial.payments} /></TabsContent>
              <TabsContent value="settings" className="mt-5"><SettingsPanel customer={customer} onSave={(c) => setCustomer({ ...customer, ...c })} /></TabsContent>
            </Tabs>
          </div>
        </div>
        <aside className="min-w-0 space-y-6 lg:col-span-4">
          <AddressCard customer={customer} onEdit={() => setDialog("address")} />
          <BillingCard onInvoices={showInvoices} />
          <ExtrasCard />
          <SupportCard />
        </aside>
      </div>

      <PauseDialog {...dlg("pause")} onConfirm={pause} />
      <SkipDialog {...dlg("skip")} onConfirm={skip} />
      {dialog === "plan" && <ChangePlanDialog {...dlg("plan")} plans={plans} onConfirm={changePlan} />}
      <CancelDialog {...dlg("cancel")} onConfirm={cancel} />
      {dialog === "address" && (
        <AddressDialog open onOpenChange={(o) => !o && close()} customer={customer} onSave={(c) => setCustomer({ ...customer, ...c })} />
      )}
    </div>
  );
}
